import { useContext } from 'react'

import {
  Box, Button, Columns, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useDispatch from '@hooks/useDispatch'
import useTemplatePage from '@hooks/useTemplatePage'

import * as templatePageActions from '@redux/modules/templatePage'

import useAssignTemplatePagePermissionsModal from './hooks/useAssignTemplatePagePermissionsModal'

import Agencies from './components/Agencies'
import BrandThemes from './components/BrandThemes'
import Providers from './components/Providers'
import Publications from './components/Publications'
import ServiceProducts from './components/ServiceProducts'
import StepNavigation from './components/StepNavigation/index'
import Services from './components/Services/Services'
import TemplateGroups from './components/TemplateGroups'
import TemplatePageDetails from './components/TemplatePageDetails'

import useFlipperKey from '@hooks/useFlipperKey'
import { TemplatePageModel, TemplatePageNewPermissions } from '@domain-group/realhub-backend-api-types'

const MODAL_KEY = 'AssignTemplatePagePermissionsModal'

const customNextSteps = {
  serviceIds: 'providerIds',
  providerIds: 'serviceProductIds',
  serviceProductIds: 'agencyIds',
  agencyIds: 'publicationIds',
  publicationIds: 'brandThemeIds',
}

type ModalCallbacks = {
  bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

export type AssignTemplatePagePermissionsModalPayload = {
  additionalTemplatePageIds?: number[],
  stepKey: string,
  templatePage: Pick<TemplatePageModel, 'id'> & Partial<TemplatePageModel>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
  payload: AssignTemplatePagePermissionsModalPayload
}

const AssignTemplatePagePermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal, payload } = props
  const { closeModal, bulkAssignTemplatePagePermissions } = callbacks || {}

  const dispatch = useDispatch()

  const modalPayload = payload
  const additionalTemplatePageIds = modalPayload?.additionalTemplatePageIds ?? []
  const initStepKey = modalPayload?.stepKey
  const initTemplatePageId = modalPayload?.templatePage?.id
  const templatePageIds = modalPayload?.additionalTemplatePageIds?.length
    ? modalPayload.additionalTemplatePageIds
    : (modalPayload?.additionalTemplatePageIds ?? [])
  const showSelectedTemplatesInSidebar = templatePageIds.length > 1

  const { isEnabled } = useFlipperKey("permissions_modal_v2")

  const assignTemplatePagePermissionsPayload = useAssignTemplatePagePermissionsModal({
    activeStepKey: initStepKey,
    customStepKeys: isEnabled ? customNextSteps : undefined,
    templatePageId: initTemplatePageId,
  })

  const {
    activeStepKey,
    callbacks: { preparePermissions, setCurrentStepKey },
    hasNextStep,
    nextStepKey,
    saveEnabled,
    templatePage,
  } = assignTemplatePagePermissionsPayload

  const templatePagePayload = useTemplatePage(templatePage)
  const { callbacks: { bulkUpdateTemplatePagePermissions: bulkUpdateFn } } = templatePagePayload

  // Save permissions to the first template, then copy them onto any remaining templates
  const bulkAssignTemplatePagePermissionsPayload = {
    callbacks: {
      action: async (permissions: TemplatePageNewPermissions) => {
        const saveResult = await bulkUpdateFn(permissions)
        if (!saveResult.success || !additionalTemplatePageIds.length || !templatePage.id) return saveResult

        return dispatch(
          templatePageActions.copyTemplatePagePermissions(templatePage.id, additionalTemplatePageIds),
        )
      },
      afterAction: closeModal,
      buildActionParams: () => [preparePermissions()],
    },
    requestOptions: templatePageIds.length
      ? { template_page_ids: templatePageIds }
      : undefined,
    toastText: 'Permissions Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} disableAnimation modalKey={MODAL_KEY} showModal={showModal} size="fullscreen">
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Permissions" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            {isEnabled && (
              <>
                <StepNavigation
                  assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload}
                  isHorizontalLayout={isEnabled}
                  showTickIcon={isEnabled}
                  newPermissionsSteps={isEnabled}
                />

                {!showSelectedTemplatesInSidebar && (
                  <TemplatePageDetails
                    additionalTemplatePageIds={additionalTemplatePageIds}
                    templatePage={templatePage}
                  />
                )}
              </>
            )}

            {activeStepKey === 'agencyIds' && (
              <Agencies assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'brandThemeIds' && (
              <BrandThemes assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'providerIds' && (
              <Providers assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'publicationIds' && (
              <Publications assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'serviceProductIds' && (
              <ServiceProducts assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'templateGroupIds' && (
              <TemplateGroups assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'serviceIds' && (
              <Services assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            {showSelectedTemplatesInSidebar && (
              <TemplatePageDetails templatePage={templatePage} additionalTemplatePageIds={templatePageIds} />
            )}

            {!isEnabled && (
              <>
                <StepNavigation
                  assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload}
                  isHorizontalLayout={isEnabled}
                  showTickIcon={isEnabled}
                />

                {!showSelectedTemplatesInSidebar && (
                  <TemplatePageDetails
                    additionalTemplatePageIds={additionalTemplatePageIds}
                    templatePage={templatePage}
                  />
                )}
              </>
            )}
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Box marginLeft="auto" width="auto">
          {hasNextStep && (
            <Button
              buttonStyle="primaryEdit"
              loadingBubbleColor="white"
              onClick={nextStepKey ? () => setCurrentStepKey(nextStepKey) : undefined}
              size="large"
              width="auto"
            >
              Next Step
            </Button>
          )}

          {(saveEnabled || isEnabled)  && (
            <Button
              buttonStyle="primaryCreate"
              loadingBubbleColor="white"
              // @ts-expect-error TS2345
              onClick={() => bulkAssignTemplatePagePermissions(bulkAssignTemplatePagePermissionsPayload)}
              marginLeft="medium"
              size="large"
              width="auto"
            >
              Save and Exit
            </Button>
          )}
        </Box>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <AssignTemplatePagePermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
